import React, { useState, useContext } from "react";
import t from "Utils/translation";
import Button from "Components/button/Button";
import cx from "classnames";
import s from "../../delivery.scss";
import { useHistory, useParams } from "react-router-dom";
import QboxmailApi from "@/qboxmailApi";
import useToggle from "Utils/useToggle";
import { alertError, alertSuccess } from "Utils/alerts";
import SmallModal from "Components/smallModal/smallModal";
import ResponsiveButtonGroup from "Components/responsiveButtonGroup/ResponsiveButtonGroup";
import TranslatedString from "Components/TranslatedString";
import Checkbox from "Components/checkbox/Checkbox";
import useSWR from "swr";
import WarningBanner from "Components/warningBanner/WarningBanner";
import { AuthContext } from "@/App";
import ConfirmModal from "Components/confirmModal/ConfirmModal";

const api = new QboxmailApi({ useCookie: true });

const ChangePlanSection = ({ accountCode, accountData, setIsForbiddenModalOpen }) => {
  const history = useHistory();

  const {
    data: pendingOrders,
    isLoading: isLoadingPendingOrders,
    isValidating: isValidatingPendingOrders,
    mutate: reloadPendingOrders,
  } = useSWR(`/delivery/accounts/${accountCode}/pending_orders`);

  const { getLoggedUser } = useContext(AuthContext);
  const loggedUser = getLoggedUser();

  const [isCancellingModalOpen, setIsCancellingModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const hasPendingOrder = pendingOrders?.plan?.length > 0 || pendingOrders?.extra?.length > 0;
  const isWaitingFirstPayment = accountData.status_detail === "wait_pay";

  const isDeleting = !!accountData.delete_on;

  const canEditAccountPlan = !(
    loggedUser.type === "TeamMember" && !loggedUser.permissions.includes("edit_account_plan")
  );

  return (
    <>
      <div className={s.dashboardItem1}>
        <div className="settingTitle">{t("delivery.accounts.serviceManagement.changePlan.title")}</div>
        <div className="sectionDesc">{t("delivery.accounts.serviceManagement.changePlan.desc")}</div>
      </div>
      <div className={s.dashboardItem2}>
        <div className="settingText">{t("delivery.accounts.serviceManagement.changePlan.info")}</div>
        <ResponsiveButtonGroup
          buttonElements={[
            <Button
              key={0}
              color="outline"
              className="mt-4"
              onClick={() => {
                if (!canEditAccountPlan) {
                  setIsForbiddenModalOpen(true);
                  return;
                }

                history.push(`/delivery/accounts/change-plan/${accountCode}`);
              }}
              disabled={hasPendingOrder || isWaitingFirstPayment || isDeleting}
              loading={isLoadingPendingOrders || isValidatingPendingOrders}
            >
              button.changePlan
            </Button>,
          ]}
          className="section"
        />
        {hasPendingOrder && !isWaitingFirstPayment && !isValidatingPendingOrders && (
          <>
            <div className="mt-6">
              <WarningBanner
                color="blue"
                msg={
                  pendingOrders?.extra?.length > 0
                    ? t("infoMsg.delivery.pendingExtradeliveryOrder")
                    : t("infoMsg.delivery.pendingUpdateOrder.modification")
                }
              />
              <a
                className="mt-2 text-[--accent] text-sm cursor-pointer block"
                onClick={() => setIsCancellingModalOpen(true)}
              >
                {t("button.modifyPlan.cancel")}
              </a>
            </div>

            <SmallModal
              closeModal={() => setIsCancellingModalOpen(false)}
              modalClassName=""
              title={t("delivery.modal.planModification.cancel.title")}
              opened={isCancellingModalOpen}
              showCloseIcon
            >
              <div className="section">{t("delivery.modal.planModification.cancel.content")}</div>
              <div className="mb-4">
                <Button
                  className="self-end"
                  type="button"
                  color="red"
                  onClick={async () => {
                    setIsCancelling(true);

                    try {
                      await api.cancelDeliveryPendingOrder(accountCode);
                      await reloadPendingOrders();
                    } catch (err) {
                      console.log(err);
                    }

                    setIsCancelling(false);
                    setIsCancellingModalOpen(false);
                  }}
                  loading={isCancelling}
                >
                  button.order.cancel
                </Button>
              </div>
            </SmallModal>
          </>
        )}
      </div>
    </>
  );
};

const DeleteSection = ({
  accountCode,
  accountData,
  toggleUpdating,
  isUpdating,
  history,
  refresh,
  setIsForbiddenModalOpen,
}) => {
  const { getLoggedUser } = useContext(AuthContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeUntilBilling, setActiveUntilBilling] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const confirmModalTitle = t("modal.delete.deliveryAccount.title");

  const loggedUser = getLoggedUser();

  const getConfirmModalText = () => {
    if (accountData.status_detail === "wait_pay") {
      return "modal.delete.deliveryAccount.text.free";
    }

    if (accountData.plan === "prepaid") {
      return "modal.delete.deliveryAccount.text.prepaid";
    }

    if (accountData.plan === "monthly") {
      return "modal.delete.deliveryAccount.text.monthly";
    }

    //free account
    return "modal.delete.deliveryAccount.text.free";
  };

  const exportSuppressionList = async () => {
    if (isExporting) return;

    const canSeeSuppressionList = !(
      loggedUser.type === "TeamMember" && !loggedUser.permissions.includes("see_suppression_list")
    );

    if (!canSeeSuppressionList) {
      setIsForbiddenModalOpen(true);
      return;
    }

    setIsExporting(true);
    const response = await api.exportSuppressionList(accountCode);
    window.open(BASE_URL_PUBLIC_ASSETS + response.file_url, "_blank");
    setIsExporting(false);
  };

  const canDeleteAccount = !(
    loggedUser.type === "TeamMember" && !loggedUser.permissions.includes("delete_delivery_accounts")
  );

  const deleteAccount = async () => {
    if (!canDeleteAccount) {
      setIsForbiddenModalOpen(true);
      return;
    }

    try {
      toggleUpdating();
      const resp = await api.deleteDeliveryAccount(accountCode, activeUntilBilling);
      if (resp.ok) {
        await refresh();
        if (!activeUntilBilling) {
          history.push("/delivery/accounts");
        }
      }

      alertSuccess(t("alert.operationSuccess"));
    } catch (error) {
      console.error(error);
      alertSuccess(t("alert.operationSuccess"));
    } finally {
      toggleUpdating();
    }
  };

  return (
    <>
      <div className={s.dashboardItem1}>
        <div className={"settingTitle"}>{t("delivery.accounts.serviceManagement.delete.title")}</div>
        <div className={"sectionDesc"}>{t("delivery.accounts.serviceManagement.delete.desc")}</div>
      </div>
      <div className={s.dashboardItem2}>
        <div className="settingText">{t("delivery.accounts.serviceManagement.delete.info")}</div>
        <div
          onClick={exportSuppressionList}
          className={cx("section", s.linkButton, {
            [s.linkButtonDisabled]: isExporting,
          })}
        >
          {accountData.status_detail === "wait_pay"
            ? null
            : isExporting
              ? t("button.export.exportingSuppressionList")
              : t("button.export.suppressionList")}
        </div>
        <ResponsiveButtonGroup
          buttonElements={[
            <Button
              color="red"
              onClick={() => {
                if (!canDeleteAccount) {
                  setIsForbiddenModalOpen(true);
                  return;
                }

                setIsModalOpen(true);
              }}
              key={0}
            >
              button.delete.deliveryAccount
            </Button>,
          ]}
          className="section"
        />
      </div>

      {/* Confirmation modal */}
      <SmallModal
        opened={isModalOpen}
        title={confirmModalTitle}
        //eslint-disable-next-line react/no-children-prop
        children={
          <div className="">
            <TranslatedString
              subs={{
                accountData: `${accountData.username} - ${accountData.description}`,
              }}
            >
              {getConfirmModalText()}
            </TranslatedString>
            {accountData.plan === "monthly" && accountData.status_detail !== "wait_pay" && (
              <div className="mt-3">
                <Checkbox
                  name="active_until_billing"
                  label={t("modal.delete.deliveryAccount.checkboxMsg")}
                  onChange={({ active_until_billing }) => setActiveUntilBilling(active_until_billing)}
                  checked={activeUntilBilling}
                />
              </div>
            )}

            {!activeUntilBilling && (
              <div className="mt-3">
                <b>{t("modal.delete.deliveryAccount.warningMsg")}</b>
              </div>
            )}
          </div>
        }
        footerComponent={
          <div className="flex justify-end flex-1 gap-2 p-3">
            <Button onClick={() => setIsModalOpen(false)} color="text">
              button.cancel
            </Button>
            <Button onClick={deleteAccount} loading={isUpdating} color="red">
              button.delete.deliveryAccount
            </Button>
          </div>
        }
      />
    </>
  );
};

const CancelDeletionSection = ({ accountCode, accountData, toggleUpdating, isUpdating, history, refresh }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  const restoreAccount = async () => {
    try {
      toggleUpdating();
      setIsRestoring(true);
      const resp = await api.restoreDeliveryAccount(accountCode);
      toggleUpdating();
      if (resp.ok) {
        await refresh();

        // mutate(
        //   //eslint-disable-next-line
        //   (key) => "/delivery/accounts?",
        //   undefined,
        //   { revalidate: false }
        // );
      }
    } catch (error) {
      console.error(error);
      alertError(t("alert.operationError"));
    }

    setIsRestoring(false);
    alertSuccess(t("alert.operationSuccess"));
  };

  return (
    <>
      <div className={s.dashboardItem1}>
        <div className={"settingTitle"}>{t("delivery.accounts.serviceManagement.restore.title")}</div>
        <div className={"sectionDesc"}>{t("delivery.accounts.serviceManagement.restore.desc")}</div>
      </div>
      <div className={s.dashboardItem2}>
        <div className="flex items-center justify-between mb-4">
          <div className="settingText">L'utenza verrà eliminata definitivamente il giorno:</div>
          <div className="!font-semibold !text-black settingText">{accountData.delete_on}</div>
        </div>

        <p
          className="settingText"
          dangerouslySetInnerHTML={{ __html: t("delivery.accounts.serviceManagement.restore.info") }}
        ></p>

        <ResponsiveButtonGroup
          buttonElements={[
            <Button color="outline" onClick={() => setIsModalOpen(true)} key={0}>
              button.restore.deliveryAccount
            </Button>,
          ]}
          className="section"
        />
      </div>

      {/* Confirmation modal */}
      <SmallModal
        opened={isModalOpen}
        title={t("modal.restore.deliveryAccount.title")}
        //eslint-disable-next-line react/no-children-prop
        children={
          <div className="">
            <TranslatedString
              subs={{
                accountData: `${accountData.username} - ${accountData.description}`,
              }}
            >
              modal.restore.deliveryAccount.text.free
            </TranslatedString>
          </div>
        }
        footerComponent={
          <div className="flex justify-end flex-1 gap-2 p-3">
            <Button onClick={() => setIsModalOpen(false)} color="text">
              button.cancel
            </Button>
            <Button onClick={restoreAccount} loading={isRestoring} color="green">
              button.restore.deliveryAccount
            </Button>
          </div>
        }
      />
    </>
  );
};

const DeleteAccount = ({ accountData, refresh }) => {
  const { accountCode, customerCode } = useParams();
  const { isLoggedUserAdmin } = useContext(AuthContext);
  const [isForbiddenModalOpen, setIsForbiddenModalOpen] = useState(false);

  const { isToggled: isUpdating, toggle: toggleUpdating } = useToggle();

  const history = useHistory();

  return (
    <div className={s.dashboardContainer}>
      {!isLoggedUserAdmin() && (
        <div className={s.dashboardContent}>
          <ChangePlanSection
            accountData={accountData}
            accountCode={accountCode}
            toggleUpdating={toggleUpdating}
            isUpdating={isUpdating}
            history={history}
            setIsForbiddenModalOpen={setIsForbiddenModalOpen}
            refresh={refresh}
          />
        </div>
      )}

      <div className={s.dashboardContent}>
        {accountData.delete_on ? (
          <CancelDeletionSection
            accountData={accountData}
            accountCode={accountCode}
            toggleUpdating={toggleUpdating}
            isUpdating={isUpdating}
            history={history}
            refresh={refresh}
          />
        ) : (
          <DeleteSection
            accountData={accountData}
            accountCode={accountCode}
            toggleUpdating={toggleUpdating}
            isUpdating={isUpdating}
            setIsForbiddenModalOpen={setIsForbiddenModalOpen}
            history={history}
            refresh={refresh}
          />
        )}
      </div>

      <ConfirmModal
        title={t("teamMembers.forbiddenAction")}
        text={t("teamMembers.forbiddenActionSubtitle")}
        textButton="button.ok"
        hideCancelButton={true}
        opened={isForbiddenModalOpen}
        action={() => setIsForbiddenModalOpen(false)}
        handleClose={() => setIsForbiddenModalOpen(false)}
        buttonColor="accent"
      />
    </div>
  );
};

export default DeleteAccount;
